import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Download } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { notify } from "@/lib/toast";
import TrialBanner from "@/components/TrialBanner";
import { logError } from "@/lib/logger";
import { QR_CANVAS_SIZE } from "@/lib/constants";

const EMPTY_STATS = { total_scans: 0, total_leads: 0, today_scans: 0, conversion: 0 };

function downloadQrPng(canvas, filename) {
    const a = document.createElement("a");
    a.download = filename;
    a.href = canvas.toDataURL("image/png");
    a.click();
}

function downloadQrSvg(canvas, filename) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${canvas.width} ${canvas.height}" width="${canvas.width}" height="${canvas.height}"><rect width="100%" height="100%" fill="white"/><image href="${canvas.toDataURL()}" width="100%" height="100%"/></svg>`;
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
}

export default function QRPage() {
    const { user, refresh } = useAuth();
    const [stats, setStats] = useState(EMPTY_STATS);
    const [msg, setMsg] = useState(user?.custom_message || "");
    const qrRef = useRef(null);

    const scanUrl = `${window.location.origin}/u/${user?.slug}`;
    const userId = user?.id;

    useEffect(() => {
        let cancelled = false;
        if (userId) {
            api.get("/stats")
                .then(({ data }) => {
                    if (cancelled) return;
                    setStats({
                        total_scans: data.total_scans,
                        total_leads: data.total_leads,
                        today_scans: data.today_scans,
                        conversion: data.conversion,
                    });
                })
                .catch((e) => logError("Stats failed", e));
        }
        setMsg(user?.custom_message || "");
        return () => { cancelled = true; };
    }, [userId, user?.custom_message]);

    const saveMessage = useCallback(async () => {
        if (!userId) return;
        try {
            await api.patch("/user/message", { custom_message: msg });
            await refresh();
            notify("Message updated");
        } catch (e) {
            notify(formatErr(e), "error");
        }
    }, [msg, userId, refresh]);

    const copy = useCallback(() => {
        navigator.clipboard.writeText(scanUrl).then(() => notify("Link copied!"));
    }, [scanUrl]);

    const download = useCallback((format) => {
        const canvas = qrRef.current?.querySelector("canvas");
        if (!canvas || !user?.slug) return;
        const fname = `connexa-${user.slug}.${format}`;
        (format === "svg" ? downloadQrSvg : downloadQrPng)(canvas, fname);
        notify("QR downloaded!");
    }, [user?.slug]);

    if (!user) return null;

    return (
        <section className="min-h-screen pt-40 pb-16 mesh-gradient" data-testid="qr-page">
            <div className="max-w-4xl mx-auto px-6">
                <TrialBanner />
                <PageHeading />
                <div className="grid lg:grid-cols-2 gap-8">
                    <QrCard qrRef={qrRef} scanUrl={scanUrl} onCopy={copy} onDownload={download} />
                    <div className="space-y-6">
                        <StatsCard stats={stats} />
                        <DetailsCard user={user} />
                        <CustomMessageCard value={msg} onChange={setMsg} onBlur={saveMessage} />
                    </div>
                </div>
            </div>
        </section>
    );
}

function PageHeading() {
    return (
        <div className="mb-10">
            <h1 className="font-display font-bold text-3xl mb-2">Your QR Code</h1>
            <p className="text-gray-400">Share this QR to start collecting WhatsApp leads</p>
        </div>
    );
}

function QrCard({ qrRef, scanUrl, onCopy, onDownload }) {
    return (
        <div className="glass rounded-3xl p-8">
            <div className="bg-white rounded-2xl p-6 mb-6 flex items-center justify-center" ref={qrRef}>
                <QRCodeCanvas value={scanUrl} size={QR_CANVAS_SIZE} level="H" includeMargin data-testid="qr-canvas" />
            </div>
            <ScanLink scanUrl={scanUrl} onCopy={onCopy} />
            <div className="flex gap-3">
                <button onClick={() => onDownload("png")} className="btn-primary flex-1 py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2" data-testid="qr-download-png">
                    <Download className="w-5 h-5" /> PNG
                </button>
                <button onClick={() => onDownload("svg")} className="glass flex-1 py-3 rounded-xl font-semibold hover:bg-white/10 transition-all flex items-center justify-center gap-2" data-testid="qr-download-svg">
                    <Download className="w-5 h-5" /> SVG
                </button>
            </div>
        </div>
    );
}

function ScanLink({ scanUrl, onCopy }) {
    return (
        <div className="text-center mb-6">
            <div className="text-sm text-gray-400 mb-2">Your unique link</div>
            <div className="bg-white/5 rounded-xl px-4 py-3 flex items-center justify-between">
                <span className="text-sm font-mono text-gray-300 truncate mr-4" data-testid="qr-link">{scanUrl}</span>
                <button onClick={onCopy} className="text-brand-400 hover:text-brand-300 text-sm font-medium flex items-center gap-1" data-testid="qr-copy">
                    <Copy className="w-4 h-4" /> Copy
                </button>
            </div>
        </div>
    );
}

function StatsCard({ stats }) {
    return (
        <div className="glass rounded-2xl p-6">
            <h3 className="font-semibold text-lg mb-4">QR Stats</h3>
            <div className="grid grid-cols-2 gap-4">
                <Stat label="Total Scans" value={stats.total_scans} testid="stat-scans" />
                <Stat label="Leads" value={stats.total_leads} testid="stat-leads" />
                <Stat label="Today" value={stats.today_scans} testid="stat-today" />
                <Stat label="Conversion" value={`${stats.conversion}%`} testid="stat-conv" />
            </div>
        </div>
    );
}

function DetailsCard({ user }) {
    return (
        <div className="glass rounded-2xl p-6">
            <h3 className="font-semibold text-lg mb-4">Your Details</h3>
            <div className="space-y-3 text-sm">
                <Row k="Name" v={user.name} />
                <Row k="Business" v={user.business || "-"} />
                <Row k="WhatsApp" v={user.phone ? "+" + user.phone : "(not set)"} />
                <Row k="Created" v={user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"} />
            </div>
        </div>
    );
}

function CustomMessageCard({ value, onChange, onBlur }) {
    return (
        <div className="glass rounded-2xl p-6">
            <h3 className="font-semibold text-lg mb-4">Customize Message</h3>
            <textarea value={value} onChange={(e) => onChange(e.target.value)} onBlur={onBlur}
                rows={3}
                className="input-field w-full px-4 py-3 rounded-xl text-sm resize-none"
                data-testid="custom-message" />
            <div className="mt-3 text-xs text-gray-500">
                Use <code className="bg-white/10 px-1 rounded">{'{name}'}</code> and <code className="bg-white/10 px-1 rounded">{'{phone}'}</code> as placeholders
            </div>
        </div>
    );
}

function Stat({ label, value, testid }) {
    return (
        <div className="bg-white/5 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold gradient-text" data-testid={testid}>{value}</div>
            <div className="text-xs text-gray-500 mt-1">{label}</div>
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex justify-between">
            <span className="text-gray-400">{k}</span>
            <span className="text-white font-medium">{v}</span>
        </div>
    );
}
